import { recordExport } from '../api.js';

import { downloadCsv, toCsv } from './register-kit.js';

/**
 * Taking a register away — recorded first, saved second.
 *
 * WHY THIS IS NOT IN `register-kit.tsx`
 * ------------------------------------
 * That file says of itself: *"What is deliberately NOT here: anything that
 * knows about a domain. No API import…"*, and it is right — a part that talks
 * to the server is a part a future register cannot reuse without dragging the
 * client in. `toCsv` and `downloadCsv` stay there, where they belong. The call
 * that records the act lives here, one file away.
 *
 * WHY THE ORDER MATTERS
 * ---------------------
 * `09` §Threats names bulk export as the way a project walks out of the door,
 * and answers it: *"Every export is audited with row count and filters; exports
 * over a threshold notify the Company Admin."* Recording after the download
 * would mean a refused export had already happened, and a failed record would
 * leave a file nobody knows about. So the server is asked first, and the file
 * is written only if it says yes.
 *
 * WHY IT IS ONE FUNCTION RATHER THAN TWO LINES IN NINE SCREENS
 * -----------------------------------------------------------
 * The tenth register's Export button will be written by copying one of the
 * nine, and the line that gets dropped in a copy is always the one with no
 * visible effect. Here there is nothing to drop: the only route to the file is
 * through the call that records it. The task register is the proof — it was
 * copied from a screen that had the permission check, arrived without one, and
 * offered every subcontractor a CSV of the register for as long as it existed.
 */
export async function exportRegister(input: {
  readonly projectId: string;
  /** The permission resource, e.g. `snag`. Becomes the audit action. */
  readonly resource: string;
  readonly filename: string;
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
  /**
   * What was showing when the file was written. Usually
   * `{ query: view.query, ...view.chosen }`.
   *
   * `09` wants these as much as the count, and it is right to: "exported 400
   * snags" and "exported 400 snags filtered to one subcontractor" are not the
   * same event.
   */
  readonly filters?: Readonly<Record<string, unknown>>;
}): Promise<void> {
  await recordExport(input.projectId, input.resource, input.rows.length, input.filters ?? {});
  downloadCsv(input.filename, toCsv(input.headers, input.rows));
}
